/**
 * FAQList — Weidner Via6 FAQ accordion for /frequently-asked-questions.
 *
 * Renders the property `faqs` collection as a vertical list of
 * expand/collapse disclosure items. When FAQ items carry a `category`
 * field, questions are grouped under category headings in the order
 * each category first appears in the collection; FAQs without a
 * category fall through to a shared "General" group (hidden entirely
 * if nothing else is categorized, so single-category sites don't get a
 * redundant heading).
 *
 * One item can be open at a time per category — opening a new question
 * closes the previously open one — matching the standard FAQ UX
 * residents expect. Answers are richtext in the collection schema, so
 * we render them with `dangerouslySetInnerHTML` (same pattern used by
 * ReviewsSlider for the `text` field).
 *
 * Visual contract:
 *   - White background, same section rhythm as the other interior
 *     content sections (`AccessibilityStatement`, `PetPolicy`).
 *   - Each category heading uses the brand `vibe-cursive-accent`
 *     treatment to echo the InteriorIntro titles on sibling pages.
 *   - Each disclosure row is a bottom-bordered band in the primary
 *     color so the active question "glows" against the surface.
 *   - Chevron rotates 180° when the row is open; height animates via
 *     a CSS grid trick (`grid-template-rows: 0fr → 1fr`) so no JS
 *     measurement is needed.
 */

import { useMemo, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  EditableCollectionBinding,
  useCollectionBinding,
} from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

interface FAQ {
  question?: string | null;
  answer?: string | null;
  category?: string | null;
  [key: string]: unknown;
}

const UNCATEGORIZED_LABEL = 'General';

function hasValue(val: unknown): val is string {
  if (val == null) return false;
  return String(val).trim() !== '';
}

interface AccordionItemProps {
  faq: FAQ;
  isOpen: boolean;
  onToggle: () => void;
  itemId: string;
}

function AccordionItem({ faq, isOpen, onToggle, itemId }: AccordionItemProps) {
  const question = hasValue(faq.question) ? faq.question : '';
  const answer = hasValue(faq.answer) ? faq.answer : '';

  return (
    <div className={`faq-item${isOpen ? ' faq-item--open' : ''}`}>
      <h3 className="faq-item-heading">
        <button
          type="button"
          className="faq-item-button"
          aria-expanded={isOpen}
          aria-controls={`${itemId}-panel`}
          id={`${itemId}-button`}
          onClick={onToggle}
        >
          <span className="faq-item-question">{question}</span>
          <span className="faq-item-icon" aria-hidden="true">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.25"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </span>
        </button>
      </h3>

      <div
        id={`${itemId}-panel`}
        role="region"
        aria-labelledby={`${itemId}-button`}
        className="faq-item-panel"
      >
        <div className="faq-item-panel-inner">
          <div
            className="faq-item-answer"
            // Answers are richtext in the `faqs` collection schema — render
            // as HTML so editor-authored emphasis, lists, and links carry
            // through. Same pattern used by `ReviewsSlider` for the
            // richtext `text` field.
            dangerouslySetInnerHTML={{ __html: answer }}
          />
        </div>
      </div>
    </div>
  );
}

interface CategoryGroup {
  category: string;
  items: FAQ[];
}

export function FAQList() {
  const faqs = useCollectionBinding<FAQ>('faqlist.items', {
    collection: 'faqs',
  });

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  // Group FAQs by category in the order each category first appears.
  // FAQs without a category fall into the "General" bucket. If every
  // item is uncategorized we hide the heading entirely so a single-
  // category list doesn't display a redundant group title.
  const groups = useMemo<CategoryGroup[]>(() => {
    const order: string[] = [];
    const buckets = new Map<string, FAQ[]>();

    for (const faq of faqs) {
      const raw = hasValue(faq.category) ? faq.category!.trim() : '';
      const key = raw || UNCATEGORIZED_LABEL;
      if (!buckets.has(key)) {
        order.push(key);
        buckets.set(key, []);
      }
      buckets.get(key)!.push(faq);
    }

    return order.map((category) => ({
      category,
      items: buckets.get(category) || [],
    }));
  }, [faqs]);

  const singleCategory = groups.length === 1;
  const onlyUncategorized =
    singleCategory && groups[0].category === UNCATEGORIZED_LABEL;

  // One open row per category. Keyed by `<groupIndex>:<itemIndex>` so
  // opening a question in one category doesn't close the one that's
  // open in another.
  const [openKey, setOpenKey] = useState<string | null>(null);

  if (faqs.length === 0) {
    return (
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12 text-[var(--vibe-color-text-muted)]">
            <p className="font-body text-lg">
              Frequently asked questions will be displayed here.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up py-16 md:py-24 bg-white"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <EditableCollectionBinding
          bindingPath="faqlist.items"
          label="FAQs"
        >
          <div className="faq-stack">
            {groups.map((group, gIdx) => (
              <div
                key={`${group.category}-${gIdx}`}
                className="faq-group"
              >
                {!onlyUncategorized && (
                  <h2 className="faq-group-heading">
                    {group.category}
                  </h2>
                )}
                <div className="faq-group-items">
                  {group.items.map((faq, iIdx) => {
                    const key = `${gIdx}:${iIdx}`;
                    return (
                      <AccordionItem
                        key={key}
                        itemId={`faq-${gIdx}-${iIdx}`}
                        faq={faq}
                        isOpen={openKey === key}
                        onToggle={() =>
                          setOpenKey((prev) => (prev === key ? null : key))
                        }
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </EditableCollectionBinding>
      </div>

      <style>{`
        .faq-stack {
          display: flex;
          flex-direction: column;
          gap: 2.5rem;
        }

        .faq-group {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .faq-group-heading {
          font-family: var(--vibe-font-heading);
          font-size: 1.75rem;
          font-weight: 400;
          line-height: 1.2;
          letter-spacing: 0.02em;
          color: var(--vibe-color-primary);
          margin: 0 0 0.5rem;
          padding-bottom: 0.75rem;
          border-bottom: 2px solid var(--vibe-color-secondary);
          text-transform: uppercase;
          text-align: center;
        }
        @media (min-width: 768px) {
          .faq-group-heading {
            font-size: 2.25rem;
            text-align: left;
          }
        }

        .faq-group-items {
          display: flex;
          flex-direction: column;
        }

        .faq-item {
          border-bottom: 1px solid var(--vibe-color-border);
        }

        .faq-item-heading {
          margin: 0;
        }

        .faq-item-button {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          padding: 1.1rem 0.25rem;
          background: transparent;
          border: none;
          cursor: pointer;
          text-align: left;
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          font-weight: 600;
          color: var(--vibe-color-primary);
          gap: 1rem;
          transition: color 150ms ease;
        }
        @media (min-width: 768px) {
          .faq-item-button {
            font-size: 1.125rem;
            padding: 1.35rem 0.25rem;
          }
        }
        .faq-item-button:hover,
        .faq-item-button:focus-visible {
          color: var(--vibe-color-secondary);
        }
        .faq-item-button:focus-visible {
          outline: 2px solid var(--vibe-color-secondary);
          outline-offset: 2px;
          border-radius: 2px;
        }

        .faq-item-question {
          flex: 1;
          min-width: 0;
        }

        .faq-item-icon {
          flex-shrink: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          color: var(--vibe-color-primary);
          transition: transform 250ms ease, color 150ms ease;
        }
        .faq-item--open .faq-item-icon {
          transform: rotate(180deg);
        }

        /* Height animation via a grid-row trick — avoids measuring the
           panel in JS. The outer panel starts at 0fr and transitions to
           1fr when open; the inner wrapper owns the actual content so
           overflow stays hidden mid-animation. */
        .faq-item-panel {
          display: grid;
          grid-template-rows: 0fr;
          transition: grid-template-rows 300ms ease;
        }
        .faq-item--open .faq-item-panel {
          grid-template-rows: 1fr;
        }
        .faq-item-panel-inner {
          overflow: hidden;
        }

        .faq-item-answer {
          padding: 0 0.25rem 1.25rem;
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.65;
          color: var(--vibe-color-text);
        }
        .faq-item-answer :first-child { margin-top: 0; }
        .faq-item-answer :last-child { margin-bottom: 0; }
        .faq-item-answer p {
          margin: 0 0 0.75rem;
        }
        .faq-item-answer ul,
        .faq-item-answer ol {
          margin: 0 0 0.75rem 1.25rem;
          padding: 0;
        }
        .faq-item-answer li {
          margin-bottom: 0.25rem;
        }
        .faq-item-answer a {
          color: var(--vibe-color-secondary);
          text-decoration: underline;
        }
        .faq-item-answer strong {
          font-weight: 600;
        }

        @media (prefers-reduced-motion: reduce) {
          .faq-item-panel,
          .faq-item-icon {
            transition: none;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FAQList',
  displayName: 'FAQ List',
  tokens: {},
  collectionBindings: {
    'faqlist.items': {
      label: 'FAQs',
      collection: 'faqs',
    },
  },
};

(FAQList as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FAQList;
